import { FC, memo, useEffect, useMemo, useState } from 'react'

import { useSetFocus } from '@please/lrud'

import { Button } from '@/components/Button/Button'
import {
  Actions,
  PaymentPopupContainer,
  PaymentPopupContent,
  ContentContainer,
  Title,
} from '@/components/PaymentPopup/styles'
import { focusIds, orientation } from '@/constants/focus'
import { creditsOptionDe, creditsOptionEn, icons } from '@/constants/icons'
import { SubscriptionsPanel } from '@/containers/Subscriptions/SubscbriptionPanel'
import { getCurrentLanguage } from '@/i18n'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import useStore from '@/store/useStore'
import { OfferType } from '@/types/servicesTypes/configTypes'
import { PaymentPopupType } from '@/types/storeTypes/commonStoreTypes'

const PaymentPopupRaw: FC = () => {
  const setFocus = useSetFocus()
  const { t } = useCommonTranslation()
  const paymentPopup = useStore((state) => state.common.paymentPopup)
  const setPaymentPopup = useStore((state) => state.common.setPaymentPopup)
  const [offers, setOffers] = useState<Nullable<OfferType[]>>(null)

  const userCreditsWallets = useStore(
    (state) => state.common.userCreditsWallets,
  )

  const swCreditCount = useMemo(() => {
    const swWallet = userCreditsWallets?.find(
      (wallet) => wallet.uniqueId === 'sportworld-credits',
    )
    return swWallet?.availableCreditCount ?? 0
  }, [userCreditsWallets])

  useEffect(() => {
    if (paymentPopup) {
      getOffers(paymentPopup)
      setTimeout(setFocus, 0, focusIds.AUTH_POPUP)
    }
    return () => setOffers(null)
  }, [paymentPopup])

  const getOffers = async (paymentPopup: PaymentPopupType) => {
    const ppvInfo = paymentPopup.event.ppvInfo

    const creditsWallet = ppvInfo?.find(({ walletId }) =>
      walletId?.includes('-credits'),
    )
    const offers = paymentPopup.offers || []

    if (ppvInfo && creditsWallet) {
      const ppvEventCost = creditsWallet?.cost
      const language = getCurrentLanguage()
      const sources = [
        {
          url: language === 'de' ? creditsOptionDe : creditsOptionEn,
          darkTheme: true,
          format: 'Jpeg',
          height: 1156,
          width: 880,
        },
      ]
      const genericCreditOffer: OfferType = {
        id: 'CreditsPackage',
        title: '',
        description: '',
        callToActionText: t('paymentPopup.continueWithCredits'),
        image: { sources },
        creditPackageInfo: {
          credits: swCreditCount,
          cost: ppvEventCost,
        },
      }
      setOffers([genericCreditOffer, ...offers])
    } else {
      setOffers(offers || [])
    }
  }

  if (!paymentPopup) {
    return null
  }

  const closeHandler = () => {
    setFocus(paymentPopup.prevFocusId)
    setPaymentPopup(null)
  }

  return (
    <PaymentPopupContainer
      orientation={orientation.VERTICAL}
      focusId={focusIds.AUTH_POPUP}
      isTrap={true}
    >
      {offers && (
        <PaymentPopupContent>
          <ContentContainer>
            <Title>{t('paymentPopup.title')}</Title>
            <SubscriptionsPanel offers={offers} />
          </ContentContainer>
          <Actions>
            <Button
              iconSrc={icons.navigation.exit}
              text={t('close')}
              onClick={closeHandler}
            />
          </Actions>
        </PaymentPopupContent>
      )}
    </PaymentPopupContainer>
  )
}

export const PaymentPopup = memo(PaymentPopupRaw)
